// frontend/src/components/CaptchaField.jsx
// A self-contained captcha field for the svg-captcha backend integrated in /api/auth?action=captcha
// Usage example:
//   <CaptchaField purpose="reset" value={captcha} onChange={setCaptcha} required />
// Then submit `captcha` along with your form fields to endpoints that verify it (e.g., send-otp, request-access).

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

export default function CaptchaField({
  purpose = "generic",          // matches `p` used server-side when issuing captcha
  value,                         // controlled input value (optional); if omitted, internal state is used
  onChange,                      // (val: string) => void
  label = "Nhập mã xác nhận / Enter verification code",
  imgAlt = "CAPTCHA image",
  className,
  required = false,
  disabled = false,
  autoFocus = false,
  onRefresh,                     // optional callback when captcha image refreshed
}) {
  const [internal, setInternal] = useState("");
  const v = value !== undefined ? value : internal;

  const [seed, setSeed] = useState(() => Date.now());
  const inputRef = useRef(null);

  const src = useMemo(() => `/api/auth?action=captcha&p=${encodeURIComponent(purpose)}&t=${seed}`,[purpose, seed]);

  const setVal = useCallback((next) => {
    if (onChange) onChange(next);
    else setInternal(next);
  }, [onChange]);

  const refresh = useCallback(() => {
    setSeed(Date.now());
    onRefresh?.();
    // keep focus on the input for quick retries
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [onRefresh]);

  // If parent toggles purpose, refresh image automatically
  // If parent toggles purpose, refresh image automatically
  useEffect(() => {
    refresh();
  }, [purpose, refresh]);

  return (
    <div className={className}>
      <div className="box" style={{ display: "grid", gap: 10 }}>
        <div className="control-row" style={{ alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <img
            src={src}
            alt={imgAlt}
            width={160}
            height={52}
            style={{ border: "1px solid #e5e5e5", borderRadius: 8 }}
            onError={(e) => {
              // soft retry after network hiccup
              setTimeout(refresh, 300);
            }}
          />
          <button type="button" className="btn" onClick={refresh} disabled={disabled}>
            🔄 Đổi mã / Refresh
          </button>
        </div>
        <label className="label" htmlFor="captcha-input">{label}</label>
        <input
          id="captcha-input"
          ref={inputRef}
          className="text-input"
          value={v}
          onChange={(e) => setVal(e.target.value)}
          placeholder="Nhập ký tự bạn thấy / Type what you see"
          inputMode="text"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          required={required}
          disabled={disabled}
          autoFocus={autoFocus}
        />
        <div style={{ fontSize: 12, opacity: 0.75 }}>
          Lưu ý: không phân biệt hoa/thường; dùng nút “Đổi mã” nếu khó đọc.
        </div>
      </div>
    </div>
  );
}